import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  skipWhile,
  startWith,
  Subscription,
  switchMap,
  throwError,
  zip,
} from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { SelectOption, TripsRequest, VatReceiptContactDetails } from '@fcom/common/interfaces';
import { ButtonMode, ButtonTheme, IconButtonSize, IconButtonTheme } from '@fcom/ui-components/components/buttons';
import { CorporateTripType, Trip } from '@fcom/common/interfaces/corporate/corporate.interface';
import { unsubscribe } from '@fcom/core/utils';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { WindowRef } from '@fcom/core/providers';
import { AppState } from '@fcom/core/interfaces';
import { profile } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx/operators/finShare';
import { Profile } from '@fcom/core-api/login';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { ScrollService, TranslatedCountryCode } from '@fcom/common/services';
import { RootPaths } from '@fcom/core/constants';
import { SentryLogger } from '@fcom/core/services';
import { LoaderTheme } from '@fcom/ui-components/components/loader';
import { PageIndexes, PaginationComponent } from '@fcom/ui-components/components/pagination/pagination.component';
import { isCorporateAdminOrTravelBooker } from '@fcom/common/login';
import { RedirectActions } from '@fcom/common-booking';
import { DrawerDirection } from '@fcom/ui-components/components/drawer/enums';
import { IconPosition } from '@fcom/ui-components/components/icons';

import { FilterDateRangeKey, getFilterDateRange } from '../../utils/utils';
import { RebookTripComponent } from '../rebook-trip/rebook-trip.component';

interface FilterForm {
  searchTerm: FormControl<string>;
  filter: FormControl<string>;
  status: FormControl<TripStatus>;
}

export interface TripRow extends Trip {
  expanded?: boolean;
  loading?: boolean;
}

export enum TripStatus {
  PAST = 'past',
  UPCOMING = 'upcoming',
}

@Component({
  selector: 'fin-trips-view',
  templateUrl: './trips-view.component.html',
  styleUrl: './trips-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripsViewComponent implements OnInit, OnDestroy {
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = CorporateTripType;
  readonly TripStatus = TripStatus;
  readonly DrawerDirection = DrawerDirection;
  readonly IconPosition = IconPosition;
  readonly itemsPerPageOptions = [10, 20, 50, 100];

  @ViewChild('rebookTrip') rebookTripRef: RebookTripComponent;
  @ViewChild('tripsWrapper') tripsWrapper: ElementRef;
  @ViewChild('paginationComponent') paginationComponent: PaginationComponent;

  itemsPerPage = this.itemsPerPageOptions[0];
  form: FormGroup<FilterForm>;
  dateRangeOptions$: Observable<SelectOption[]>;
  dateRangeTranslations$: Observable<string[]>;
  trips$: BehaviorSubject<TripRow[]> = new BehaviorSubject<TripRow[]>(null);
  profile$: Observable<Profile>;
  isAdminOrTravelBooker$: Observable<boolean>;
  totalAmount: number;
  resultsLoading = false;
  pageIndex$: BehaviorSubject<PageIndexes> = new BehaviorSubject<PageIndexes>({
    startIndex: 0,
    endIndex: 10,
  });
  showMyTrips$ = new BehaviorSubject<boolean>(false);
  hasActiveFilters$: Observable<boolean>;
  showTripsTable$: Observable<boolean>;
  selectedTrip: Trip;
  drawerOpen: boolean;
  subscriptions = new Subscription();

  constructor(
    private windowRef: WindowRef,
    private corporateService: CorporateService,
    private languageService: LanguageService,
    private scrollService: ScrollService,
    private store$: Store<AppState>,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private sentryLogger: SentryLogger,
    private currentRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profile(), finShare());
    this.isAdminOrTravelBooker$ = this.profile$.pipe(map((p) => isCorporateAdminOrTravelBooker(p)));
    this.form = new FormGroup<FilterForm>({
      searchTerm: new FormControl<string>(''),
      filter: new FormControl<string>(FilterDateRangeKey.ALL),
      status: new FormControl<TripStatus>(TripStatus.UPCOMING),
    });
    this.dateRangeTranslations$ = this.languageService
      .translateMultiple(
        ['all', 'nextWeek', 'nextMonth', 'nextYear', 'pastYear', 'pastMonth', 'pastWeek'].map(
          (postFix) => `CORPORATE.corporatePortal.tripsView.filterTrips.options.${postFix}`
        )
      )
      .pipe(finShare());
    this.setDateRangeOptions(TripStatus.UPCOMING);
    const searchTerms$ = combineLatest([
      this.pageIndex$,
      this.getSearchTermFilter(),
      this.getDateRangeFilter(),
      this.showMyTrips$,
    ]);
    this.hasActiveFilters$ = searchTerms$.pipe(
      map(([_page, searchTerm, dateRange, showMyTrips]) => {
        return Boolean(searchTerm) || dateRange !== FilterDateRangeKey.ALL || showMyTrips;
      })
    );
    this.showTripsTable$ = combineLatest([this.hasActiveFilters$, this.trips$]).pipe(
      map(([hasFilters, trips]) => {
        return Boolean(hasFilters) || Boolean(trips);
      })
    );
    this.subscriptions.add(
      this.form.controls.status.valueChanges.subscribe((tripStatus) => {
        this.setDateRangeOptions(tripStatus);
      })
    );
    this.subscriptions.add(
      searchTerms$
        .pipe(
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          switchMap(([page, searchTerm, dateRange, showMyTrips]) =>
            zip(this.loadTrips(this.getTripRequestParams(page, searchTerm, dateRange, showMyTrips)), of(searchTerm))
          ),
          tap(([trips, filter]: [TripRow[], string]) => {
            if (filter) {
              trips.forEach((trip) => (trip.expanded = true));
            }
          })
        )
        .subscribe(([trips]) => {
          this.trips$.next(trips);
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  changePage(page: PageIndexes): void {
    this.scrollService.scrollToImmediate(this.tripsWrapper, 120, false, true);
    this.pageIndex$.next(page);
  }

  openRebookModal(trip: TripRow): void {
    this.drawerOpen = false;
    this.rebookTripRef.openModal(trip);
  }

  downloadVat(trip: TripRow): void {
    trip.loading = true;
    const pnr = {
      lastName: trip.passengers[0].lastName,
      pnr: trip.bookingReference,
    };
    this.subscriptions.add(
      this.getContactDetails()
        .pipe(
          switchMap((contactDetails) => {
            return this.corporateService.getReceiptUrl(pnr, contactDetails).pipe(
              take(1),
              catchError((err: unknown) => {
                trip.loading = false;
                this.cdr.markForCheck();
                this.sentryLogger.error('Failed to download VAT receipt', {
                  err,
                });
                return throwError(() => err);
              })
            );
          })
        )
        .subscribe((url) => {
          trip.loading = false;
          this.cdr.markForCheck();
          this.windowRef.nativeWindow.open(url, '_blank');
        })
    );
  }

  openTripDetails(trip: TripRow): void {
    this.selectedTrip = trip;
    this.drawerOpen = true;
  }

  manageBooking(trip: TripRow): void {
    this.store$.dispatch(
      RedirectActions.setPNR({ pnr: { lastName: trip.passengers[0].lastName, recLoc: trip.bookingReference } })
    );
    this.router.navigate([this.languageService.langValue, RootPaths.MANAGE_BOOKING_ROOT]);
  }

  private setDateRangeOptions(tripStatus: TripStatus): void {
    this.dateRangeOptions$ = this.dateRangeTranslations$.pipe(
      map((translations) => {
        if (tripStatus === TripStatus.UPCOMING) {
          this.form.controls.filter.setValue(FilterDateRangeKey.ALL);
          return [
            { name: translations[0], value: FilterDateRangeKey.ALL },
            { name: translations[1], value: FilterDateRangeKey.NEXT_WEEK },
            { name: translations[2], value: FilterDateRangeKey.NEXT_MONTH },
            { name: translations[3], value: FilterDateRangeKey.NEXT_YEAR },
          ];
        } else {
          this.form.controls.filter.setValue(FilterDateRangeKey.PAST_YEAR);
          return [
            { name: translations[4], value: FilterDateRangeKey.PAST_YEAR },
            { name: translations[5], value: FilterDateRangeKey.PAST_MONTH },
            { name: translations[6], value: FilterDateRangeKey.PAST_WEEK },
          ];
        }
      })
    );
    this.paginationComponent?.reset();
  }

  private getSearchTermFilter(): Observable<string> {
    const ctrl = this.form.controls.searchTerm;
    return of(ctrl.value).pipe(
      switchMap((initialValue) =>
        ctrl.valueChanges.pipe(
          skipWhile((value) => value.length < 2),
          map((value) => (value.length < 2 ? '' : value)),
          distinctUntilChanged(),
          debounceTime(1000),
          startWith(initialValue)
        )
      ),
      finShare()
    );
  }

  private getDateRangeFilter(): Observable<FilterDateRangeKey> {
    const ctrl = this.form.controls.filter;
    return ctrl.valueChanges.pipe(
      startWith(ctrl.value),
      map((v) => v as FilterDateRangeKey),
      finShare()
    );
  }

  private getContactDetails(): Observable<VatReceiptContactDetails> {
    return combineLatest([this.profile$, this.languageService.translate('countryCodes')]).pipe(
      map(([userProfile, countryCodes]: [Profile, TranslatedCountryCode[]]) => ({
        companyName: userProfile.corporate.name,
        companyAddress: userProfile.corporate.street,
        companyPostalCode: userProfile.corporate.zipcode,
        companyCity: userProfile.corporate.city,
        companyCountry: countryCodes.find((cc) => cc.countryCode === userProfile.corporate.country)?.name,
        companyBusinessId: userProfile.corporate.businessId,
      }))
    );
  }

  private getTripRequestParams(
    page: PageIndexes,
    searchTerm: string,
    dateRangeKey: FilterDateRangeKey,
    showMyTrips: boolean
  ): TripsRequest {
    return {
      from: page.startIndex,
      size: page.endIndex - page.startIndex,
      fromSaved: Boolean(this.currentRoute.snapshot.queryParams.fromSaved),
      filter: {
        myTrips: showMyTrips,
        ...(searchTerm?.length > 0 ? { searchTerm } : undefined),
        ...(dateRangeKey ? { dateRange: getFilterDateRange(dateRangeKey) } : undefined),
      },
    };
  }

  private loadTrips(searchParams: TripsRequest): Observable<Trip[]> {
    this.resultsLoading = true;
    return this.corporateService.getTrips(searchParams).pipe(
      tap((response) => {
        this.totalAmount = response.pagination.total;
        this.resultsLoading = false;
      }),
      map((response) => response.trips)
    );
  }
}
